/**
 * @fileoverview Inspector roster widget with primary-inspector selection.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Render the active inspection roster and allow exactly one inspector to be
 *   marked as primary.
 * - Persist the selected primary inspector in a normal field response value so
 *   mobile/tenant conduct flows can override logged-in-user attribution.
 * - Merge host-provided event inspectors with static template options.
 *
 * Dependencies:
 * - React 18 (peer).
 * - `FieldWrapper` for canonical widget chrome.
 * - Inspector roster context from renderer providers.
 *
 * Architecture:
 * - Presentation-only. State is owned by the host PersistAdapter through the
 *   normal `onChange` field response path.
 *
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - Emits controlled field value changes to the host renderer adapter; the
 *   component does not persist directly.
 *
 * Sync Strategy:
 * - Host inspection draft/session sync handles persistence and reconciliation.
 *
 * Conflict Resolution:
 * - Host response conflict handling applies to the emitted field value.
 *
 * Notes:
 * - This widget does not create users. It only selects from the event/template
 *   roster the host provides.
 */
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';
import { useInspectorRoster } from '../contracts/rendererContext';
import {
  mergeInspectorRosters,
  normalizeInspectorSelectionValue,
  type InspectorRosterEntry,
  type InspectorSelectionValue,
} from '../inspectors/inspectorSelection';

interface InspectorFieldExtras {
  inspectorOptions?: InspectorRosterEntry[];
  options?: InspectorRosterEntry[];
}

function primaryName(roster: InspectorRosterEntry[], primaryInspectorId?: string): string | undefined {
  return roster.find((entry) => entry.id === primaryInspectorId)?.name;
}

/**
 * @component InspectorField
 *
 * Purpose:
 * - Allows conduct-mode users to select the inspection's primary inspector from
 *   a host-provided roster.
 *
 * Props:
 * - field: canonical inspection field metadata.
 * - value: current inspector-selection response value.
 * - onChange: host callback that persists the selected primary inspector.
 * - resolved/isNA/onToggleNA/errorMessage/requiredUnmet: canonical renderer
 *   field state and validation controls.
 *
 * Behavior:
 * - Merges persisted, host, and template rosters and emits exactly one primary
 *   marker when a user clicks "Make primary".
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Does not create users, call APIs, or persist outside the renderer response
 *   adapter.
 *
 * Performance:
 * - Renders a small roster; no memoization is required.
 */
export function InspectorField(props: BaseWidgetProps<InspectorSelectionValue | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, errorMessage, requiredUnmet } = props;
  const hostRoster = useInspectorRoster();
  const extras = (field.extras ?? {}) as InspectorFieldExtras;
  const selection = normalizeInspectorSelectionValue(value);
  const roster = mergeInspectorRosters(
    selection?.inspectors,
    hostRoster,
    extras.inspectorOptions,
    extras.options,
  );
  const primaryInspectorId = selection?.primaryInspectorId
    ?? roster.find((entry) => entry.isPrimary)?.id;

  function markPrimary(entry: InspectorRosterEntry) {
    if (resolved?.disabled) return;
    const nextRoster = roster.map((candidate) => ({
      ...candidate,
      isPrimary: candidate.id === entry.id,
    }));
    onChange({
      valueKind: 'inspectorSelection',
      inspectors: nextRoster,
      primaryInspectorId: entry.id,
      primaryInspectorName: entry.name,
      updatedAt: new Date().toISOString(),
    });
  }

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="inspectors"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      errorMessage={errorMessage}
      requiredUnmet={requiredUnmet} requiredImmediate={props.requiredImmediate}
    >
      <div
        data-inspector-roster
        role="list"
        aria-label="Inspection roster"
        className="rounded-sm border border-slate-200 bg-white divide-y divide-slate-100"
      >
        {roster.length === 0 ? (
          <div className="px-3 py-2 text-xs text-slate-500">No inspectors are assigned to this inspection.</div>
        ) : roster.map((entry) => {
          const isPrimary = entry.id === primaryInspectorId;
          return (
            <div
              key={entry.id}
              role="listitem"
              data-inspector-id={entry.id}
              data-primary-inspector={isPrimary ? 'true' : 'false'}
              className="flex items-center justify-between gap-3 px-3 py-2"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{entry.name}</div>
                {entry.role && <div className="text-xs text-slate-500 truncate">{entry.role}</div>}
              </div>
              {entry.status && (
                <span data-inspector-status={entry.status} className={`rounded-full px-2 py-1 text-[10px] font-semibold ${entry.status === 'online' ? 'bg-emerald-100 text-emerald-800' : entry.status === 'offline' ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-800'}`}>
                  {entry.status}
                </span>
              )}
              <button
                type="button"
                onClick={() => markPrimary(entry)}
                disabled={!!resolved?.disabled}
                data-action="mark-primary-inspector"
                aria-pressed={isPrimary}
                className={`shrink-0 px-2 py-1 rounded-full text-xs font-semibold ${
                  isPrimary
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-50'
                }`}
              >
                {isPrimary ? 'Primary' : 'Make primary'}
              </button>
            </div>
          );
        })}
      </div>
      {primaryInspectorId && (
        <p data-primary-inspector-summary className="mt-1 text-xs text-slate-600">
          Primary inspector: {primaryName(roster, primaryInspectorId) ?? primaryInspectorId}
        </p>
      )}
    </FieldWrapper>
  );
}
